// Selects the local ASR backend (Vosk by default, NeMo when toggled) behind one
// shared Asr interface. Resolves the createAsr function, its create options, and
// the asset manifest so callers (LocalPlacementAsrHandler, App loading gate) stay
// backend-agnostic.

import { FEATURE_FLAGS } from '../../constants/featureFlags';
import { createAsr as createNemoAsr } from './engine/asr';
import { getLocalAsrCreateOptions } from './localAsrConfig';
import { ASR_ASSET_MANIFEST } from './asrAssetManifest';
import { createVoskAsr, probeVoskIdbfsModelPresence } from './vosk/voskAsr';
import { getVoskCreateOptions, resolveVoskAssetManifest } from './voskAssetManifest';

export const LOCAL_ASR_BACKEND = Object.freeze({
  Vosk: 'vosk',
  Nemo: 'nemo',
});

/**
 * Resolves the active backend id from the flag, defaulting to Vosk. Unknown
 * values fall back to Vosk.
 * @returns {string}
 */
export function getLocalAsrBackendId() {
  const raw = String(FEATURE_FLAGS.LOCAL_ASR_BACKEND || LOCAL_ASR_BACKEND.Vosk).toLowerCase();
  return raw === LOCAL_ASR_BACKEND.Nemo ? LOCAL_ASR_BACKEND.Nemo : LOCAL_ASR_BACKEND.Vosk;
}

/**
 * @returns {{id: string, createAsr: Function, createOptions: Function, manifest: object}}
 */
export function resolveLocalAsrBackend() {
  if (getLocalAsrBackendId() === LOCAL_ASR_BACKEND.Nemo) {
    return {
      id: LOCAL_ASR_BACKEND.Nemo,
      createAsr: createNemoAsr,
      createOptions: getLocalAsrCreateOptions,
      manifest: ASR_ASSET_MANIFEST,
    };
  }
  return {
    id: LOCAL_ASR_BACKEND.Vosk,
    createAsr: createVoskAsr,
    createOptions: getVoskCreateOptions,
    // Resolved at call time (always the SIMD vosk-v3 manifest; the resolver also
    // logs a loud failure if it is ever reached without SIMD — the enablement
    // gate should have routed that device to server Kaldi).
    manifest: resolveVoskAssetManifest(),
  };
}

/**
 * Asset manifest for the active backend; used by the loading gate to pre-cache.
 * @returns {object}
 */
export function getActiveAsrManifest() {
  return resolveLocalAsrBackend().manifest;
}

/**
 * Whether the active backend's model is already persisted on-device (TT-1313 / CR-10679):
 * App plans the gated engine boot (estimated bar, hard timeout) on the answer and logs it
 * as the cold/warm proxy. Vosk asks its IDBFS probe about the active manifest's acoustic
 * archive; NeMo keeps its model in Cache Storage only, so there is nothing to probe.
 * Never rejects.
 * @returns {Promise<{presence: 'warm'|'cold'|'unknown', entries: number|null}>} `entries`
 *   is the probe's raw IDBFS record count when it read the store (logged for threshold
 *   validation), else null.
 */
export async function probeLocalAsrModelPresence() {
  const unknown = { presence: 'unknown', entries: null };
  if (getLocalAsrBackendId() !== LOCAL_ASR_BACKEND.Vosk) {
    return unknown;
  }
  try {
    const answer = await probeVoskIdbfsModelPresence(resolveVoskAssetManifest().modelUrl);
    return answer && typeof answer === 'object' ? answer : unknown;
  } catch {
    return unknown;
  }
}
